import { Box } from '@zoopla-eng/zds-components/box';
import { SearchContext } from '@zoopla/listing-search-context';
import { MobileQuickSearchButton } from '@zoopla/listing-search/button';
import { FiltersModal } from '@zoopla/listing-search/filters/modal';
import { ModalManager } from '@zoopla/listing-search/modal-manager';
import { SearchInitiation } from '@zoopla/listing-search/search-initiation';
import { triggerTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';
import { FC, PropsWithChildren, useContext, useRef, useState } from 'react';
import {
    desktopSubHeaderWrapperStyle,
    locationWrapperStyle,
    searchInputWrapperStyle,
    stickyDesktopSubHeaderDynamicShadowStyle,
    stickyDesktopSubHeaderWrapperStyle,
    stickyQuickSearchBarDynamicShadowStyle,
    stickyQuickSearchBarWrapperStyle,
    styledMobileQuickSearchBarStyle
} from './Layout.styles.css';
import { desktopFiltersButtonWrapperStyle } from './SearchAndFiltersWrapper.styles.css';

enum LayoutVersion {
    Mobile = 'mobile',
    Desktop = 'desktop'
}

const mobileModalUpdateResultsText = 'Update results';

type SearchAndFiltersWrapperProps = {
    showFiltersOnDesktop?: boolean;
};

const SearchAndFiltersWrapper: FC<
    PropsWithChildren<SearchAndFiltersWrapperProps>
> = ({ children, showFiltersOnDesktop = false }) => {
    const { state } = useContext(SearchContext);
    // disable interaction with filters until router.isReady
    const { isReady } = useRouter();

    const container = useRef<HTMLDivElement>(null);

    const [isModalShowing, setIsModalShowing] = useState(false);

    const openModal = () => {
        setIsModalShowing(true);
    };

    const closeModal = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'search_filters',
            eventAction: 'click',
            eventLabel: 'quick_search_bar_close'
        });

        setIsModalShowing(false);
    };

    const { searchType } = state;

    return (
        <Box fw ref={container}>
            <div
                className={stickyQuickSearchBarDynamicShadowStyle}
                data-testid="search-and-filters"
            >
                <nav className={stickyQuickSearchBarWrapperStyle}>
                    <div className={styledMobileQuickSearchBarStyle}>
                        <div className={locationWrapperStyle}>
                            <MobileQuickSearchButton
                                openModal={openModal}
                                searchType={searchType}
                            />
                        </div>
                        <FiltersModal
                            version={LayoutVersion.Mobile}
                            testId="mobile-sub-header_filters-button"
                            container={container}
                            disabled={!isReady}
                        />
                    </div>
                </nav>
            </div>
            {showFiltersOnDesktop && (
                <div
                    className={desktopSubHeaderWrapperStyle({
                        isStickyDesktopBar: false
                    })}
                    data-testid="search-desktop-subheader"
                >
                    <div className={searchInputWrapperStyle}>
                        <SearchInitiation
                            searchType={searchType}
                            eventCategory="search results"
                            searchSource="refine"
                        />
                    </div>

                    <div className={desktopFiltersButtonWrapperStyle}>
                        <FiltersModal
                            version={LayoutVersion.Desktop}
                            testId="search-results-header_filters-button"
                            container={container}
                            disabled={!isReady}
                        />
                    </div>
                </div>
            )}
            {!showFiltersOnDesktop && (
                <div
                    className={stickyDesktopSubHeaderDynamicShadowStyle}
                    data-testid="search-and-filters"
                >
                    <div className={stickyDesktopSubHeaderWrapperStyle}>
                        <div
                            className={desktopSubHeaderWrapperStyle({
                                isStickyDesktopBar: true
                            })}
                            data-testid="search-desktop-subheader"
                        >
                            <div className={searchInputWrapperStyle}>
                                <SearchInitiation
                                    searchType={searchType}
                                    eventCategory="search results"
                                    searchSource="refine"
                                />
                            </div>
                            <div className={desktopFiltersButtonWrapperStyle}>
                                <FiltersModal
                                    version={LayoutVersion.Desktop}
                                    testId="search-results-header_filters-button"
                                    container={container}
                                    disabled={!isReady}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {children}
            <ModalManager
                modalProps={{
                    updateResultsText: mobileModalUpdateResultsText,
                    // enableSearchTypeTabs: false,
                    searchType,
                    eventCategory: 'search results',
                    searchSource: 'facets'
                }}
                isModalShowing={isModalShowing}
                closeModal={closeModal}
                container={container}
            />
        </Box>
    );
};

export default SearchAndFiltersWrapper;
